import { numFont } from '../../../utils/fonts';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import InventoryOutlined from '@mui/icons-material/InventoryOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import { formatPriceWithCurrency } from '../../../utils/vat';
import { PartSectionHeader, PartCandidateRow } from '../../shared/PartSourceSections';

// The unified "Mistä osa otetaan" picker: one dialog for every part row, with the
// workshop's own register, vendor catalogues, live suppliers and the manufacturer's
// (Ezyvin) suggested alternatives as clearly separated sections.
export default function PartSwapModal({ open, onClose, part, onSelect, organizationId }) {
  const { t } = useTranslation();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open || !part) return;
    fetchCandidates();
  }, [open, part]);

  const fetchCandidates = async () => {
    setLoading(true);
    try {
      const params = { organization: organizationId };
      if (part.ezyvin_part_number) params.ezyvin_part_number = part.ezyvin_part_number;
      if (part.oe_number) params.oe = part.oe_number;
      // All of the row's variant codes — the register/catalogue probes cover every one
      const variantCodes = [...new Set(
        (part.ezyvin_variants || []).filter(v => !v.is_synthetic && v.part_number).map(v => v.part_number)
      )].slice(0, 20);
      if (variantCodes.length) params.codes = variantCodes.join(',');

      const resp = await axios.get('/api/oem/parts/swap-candidates/', { params });
      setCandidates(resp.data.candidates || []);
      setLoadError(false);
    } catch (err) {
      // A transient failure must not masquerade as the factual "no alternatives
      // found" — that pushes the mechanic to leave a missing part on the order.
      setCandidates([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  // Manufacturer's own alternatives come from the preview payload — no fetch needed
  const ezyvinCandidates = React.useMemo(() => {
    const seen = new Set();
    const unique = [];
    for (const v of part?.ezyvin_variants || []) {
      if (v.is_synthetic || !v.part_number) continue;
      const key = `${v.part_number}|${v.supplier_name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push({
        source: 'ezyvin',
        article_code: v.part_number,
        part_id: null,
        part_name: v.part_name || part?.ezyvin_part_name,
        sell_price: null,
        brand: v.supplier_name,
        supplier_name: '',
        in_stock: false,
        variant: v,
      });
    }
    unique.sort((a, b) => (a.brand || '').localeCompare(b.brand || ''));
    return unique;
  }, [part]);

  const matchesSearch = (c) =>
    !search ||
    c.part_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.article_code?.toLowerCase().includes(search.toLowerCase()) ||
    c.brand?.toLowerCase().includes(search.toLowerCase());

  const sections = [
    {
      key: 'catalog',
      title: t('oem.workshopCatalog'),
      help: t('oem.sourceOwnHelp'),
      icon: <InventoryOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />,
      candidates: candidates.filter(c => c.source === 'catalog').filter(matchesSearch),
    },
    {
      key: 'catalogue',
      title: t('oem.vendorCataloguesSection'),
      help: t('oem.sourceCatalogueHelp'),
      icon: <MenuBookOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />,
      candidates: candidates.filter(c => c.source === 'catalogue').filter(matchesSearch),
    },
    {
      key: 'supplier',
      title: t('oem.supplierParts'),
      help: t('oem.sourceSupplierHelp'),
      icon: <LocalShippingOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />,
      candidates: candidates.filter(c => c.source === 'supplier').filter(matchesSearch),
    },
    {
      key: 'ezyvin',
      title: t('oem.manufacturerSuggestions'),
      help: t('oem.sourceEzyvinHelp'),
      icon: <AutoAwesomeOutlined sx={{ fontSize: 16, color: 'secondary.main' }} />,
      candidates: ezyvinCandidates.filter(matchesSearch),
    },
  ];
  const totalShown = sections.reduce((sum, s) => sum + s.candidates.length, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
          {t('oem.partSourceTitle')} — {part?.ezyvin_part_name}
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseOutlined /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={t('oem.searchParts')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {sections.map(section => (
              section.candidates.length > 0 && (
                <CandidateSection
                  key={section.key}
                  title={section.title}
                  help={section.help}
                  icon={section.icon}
                  candidates={section.candidates}
                  onSelect={onSelect}
                />
              )
            ))}

            {totalShown === 0 && (
              <Typography sx={{ textAlign: 'center', color: loadError ? 'warning.main' : 'text.secondary', py: 3, fontSize: 13 }}>
                {loadError ? t('oem.candidatesLoadFailed') : t('oem.noCandidatesFound')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CandidateSection({ title, help, icon, candidates, onSelect }) {
  const { t } = useTranslation();
  return (
    <Box>
      <PartSectionHeader icon={icon} title={title} help={help} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {candidates.map((c, i) => (
          <PartCandidateRow
            key={`${c.article_code}-${i}`}
            onClick={() => onSelect(c)}
            primary={c.part_name}
            secondary={[c.article_code, c.brand, c.supplier_name].filter(Boolean).join(' · ')}
            trailing={
              <Box sx={{ textAlign: 'right' }}>
                {c.sell_price ? (
                  <Typography sx={{ fontSize: 13, fontWeight: 600, ...numFont }}>
                    {formatPriceWithCurrency(c.sell_price)}
                  </Typography>
                ) : c.purchase_price ? (
                  <Typography sx={{ fontSize: 12, fontWeight: 600, ...numFont, color: 'text.secondary' }}>
                    {t('oem.purchaseShort')} {formatPriceWithCurrency(c.purchase_price)}
                  </Typography>
                ) : null}
                {c.in_stock && (
                  <Typography sx={{ fontSize: 10, color: '#2f8c54', fontWeight: 500 }}>
                    <CheckCircleOutlined sx={{ fontSize: 10, mr: 0.25, verticalAlign: 'middle' }} />
                    {' '}{c.supplier_name || ''}
                  </Typography>
                )}
              </Box>
            }
          />
        ))}
      </Box>
    </Box>
  );
}
